import { useState, useEffect, useRef } from 'react';
import { Product, UseProductsResult, UseProductsParams } from '../types';
import { ProductService } from '../services/productService';

export function useProducts(params: UseProductsParams = {}): UseProductsResult {
  const { departmentId, categoryId, search, page = 1, sort, enabled = true, size = 15} = params;
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(page);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const lastFetchRef = useRef<string>('');


  useEffect(() => {
    // Skip fetching if disabled
    if (!enabled) {
      setLoading(false);
      return;
    }

    // Create a unique key for this fetch to prevent duplicates within the same component instance
    const fetchKey = `${page}-${departmentId || ''}-${categoryId || ''}-${search || ''}-${sort || ''}-${size || ''}`;
    if (lastFetchRef.current === fetchKey) {
      return;
    }
    lastFetchRef.current = fetchKey;

    let isMounted = true;

    const fetchProducts = async () => {
      // Wait for auth state to resolve so guestPrefix() returns the correct value
      if (!window.__USER_FETCHED__) {
        await new Promise<void>((resolve) => {
          const interval = setInterval(() => {
            if (window.__USER_FETCHED__) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
        });
      }

      if (!isMounted) return;

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        const response = await ProductService.getProducts(page, departmentId, categoryId, search, sort, size);

        if (isMounted) {
          setProducts(response.data);
          setCurrentPage(response.currentPage);
          setTotalPages(response.totalPages);
          setTotal(response.total);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load products';
        if (isMounted) {
          setError(errorMessage);
        }
        console.error('Error fetching products:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProducts();

    return () => {
      isMounted = false;
    };
  }, [page, departmentId, categoryId, search, sort, enabled, size]);

  return {
    products,
    loading,
    error,
    currentPage,
    totalPages,
    total,
  };
}